import React, { useState, useEffect, useMemo } from 'react';
import config from '../../config';
import axios from 'axios';
import { toast } from 'react-toastify';

// MUI Components
import {
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
} from '@mui/material';

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');

export default function AddonMaster() {
  const token = sessionStorage.getItem('token');

  // ==================== ADD FORM STATES ====================
  const [addonName, setAddonName] = useState('');
  const [price, setPrice] = useState('');
  const [gst, setGst] = useState('');
  const [saving, setSaving] = useState(false);

  // ==================== TABLE STATES ====================
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ==================== EDIT DIALOG STATES ====================
  const [editOpen, setEditOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [updating, setUpdating] = useState(false);

  // #16: item -> add-on mapping
  const [menuItems, setMenuItems] = useState([]);
  const [mapItemId, setMapItemId] = useState('');
  const [mapAddonIds, setMapAddonIds] = useState([]);
  const [allMappings, setAllMappings] = useState([]); // [{itemId, id}]
  const [mapSaving, setMapSaving] = useState(false);

  useEffect(() => {
    if (!company?.id) return;
    fetch(`${config.API_BASE_URL}/Home/GetallItemsAsync/${company.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => setMenuItems(Array.isArray(d) ? d : []))
      .catch(() => setMenuItems([]));
    fetch(`${config.API_BASE_URL}/qrmenu/item-addons/${company.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => setAllMappings(Array.isArray(d) ? d : []))
      .catch(() => setAllMappings([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When an item is picked, load its current add-on selection.
  const pickMapItem = (itemId) => {
    setMapItemId(itemId);
    setMapAddonIds(
      allMappings.filter((m) => m.itemId === Number(itemId)).map((m) => m.id),
    );
  };

  const saveItemAddons = async () => {
    if (!mapItemId) {
      toast.info('Select an item first');
      return;
    }
    setMapSaving(true);
    try {
      await axios.post(
        `${config.API_BASE_URL}/qrmenu/item-addons`,
        {
          CompanyId: company?.id,
          ItemId: Number(mapItemId),
          AddonIds: mapAddonIds,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // refresh mapping cache
      const r = await fetch(
        `${config.API_BASE_URL}/qrmenu/item-addons/${company.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setAllMappings((await r.json()) || []);
      toast.success('Item add-ons saved');
    } catch {
      toast.error('Failed to save item add-ons');
    } finally {
      setMapSaving(false);
    }
  };

  // ==================== FETCH ADDONS ====================
  const fetchAddons = async () => {
    if (!company?.id || !token) {
      setAddons([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/Home/GetaalAddonReport/${company.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const data = res.data;
      let list = [];
      if (Array.isArray(data)) list = data;
      else if (data?.data) list = data.data;
      else if (data?.result) list = data.result;
      else list = [];

      setAddons(list);
    } catch (err) {
      console.error('Failed to fetch addons:', err);
      setAddons([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAddons();
  }, []);

  // ==================== SAVE ADDON ====================
  const saveAddon = async () => {
    if (!addonName.trim() || !price.toString().trim()) {
      toast.error('Please fill all fields');
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${config.API_BASE_URL}/Home/SaveAddon/${encodeURIComponent(addonName)}/${encodeURIComponent(price)}/${encodeURIComponent(company?.id)}?gst=${encodeURIComponent(Number(gst) || 0)}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      toast.success('Addon saved successfully!');
      setAddonName('');
      setPrice('');
      setGst('');
      fetchAddons();
    } catch (err) {
      console.error('Error saving Addon:', err.response || err);
      toast.error(
        err.response?.data ||
          'Failed to save Addon. Check console for details.',
      );
    } finally {
      setSaving(false);
    }
  };

  // ==================== TABLE LOGIC ====================
  const filteredAddons = useMemo(() => {
    const searchLower = search.toLowerCase().trim();
    return addons.filter((addon) =>
      addon?.name?.toLowerCase().includes(searchLower),
    );
  }, [addons, search]);

  const displayedAddons = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredAddons.slice(start, start + rowsPerPage);
  }, [filteredAddons, page, rowsPerPage]);

  // ==================== EXPORT ====================
  const exportToExcel = () => {
    if (filteredAddons.length === 0) {
      toast.info('No addons to export');
      return;
    }

    const formatted = filteredAddons.map((a, index) => ({
      '#': index + 1,
      'Addon Name': a.name || '',
      Price: a.price ?? '',
      'Created Date': a.created
        ? new Date(a.created).toLocaleDateString('en-GB')
        : '-',
    }));

    const ws = XLSX.utils.json_to_sheet(formatted);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Addons');
    XLSX.writeFile(wb, 'AddonReport.xlsx');
  };

  // ==================== EDIT / UPDATE ====================
  const openEdit = (item) => {
    setEditItem({ ...item });
    setEditOpen(true);
  };

const updateAddon = async () => {
  if (!editItem?.id || !editItem?.name?.trim()) {
    toast.error('Addon name is required');
    return;
  }

  setUpdating(true);
  try {
    await axios.put(
      `${config.API_BASE_URL}/Home/UpdateAAdon/${editItem.id}`,
      {
        Id: editItem.id,
        Name: editItem.name.trim(),
        Price: Number(editItem.price),
        Gst: Number(editItem.gst) || 0,
        Companyid: company?.id,
        Created: editItem.created || new Date().toISOString(),
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json', // this is required
        },
      },
    );

    setEditOpen(false);
    fetchAddons();
  } catch (err) {
    console.error('Failed to update addon:', err);
    toast.error('Failed to update addon');
  } finally {
    setUpdating(false);
  }
};

  // ==================== RENDER ====================
  if (loading) {
    return (
      <Box display='flex' justifyContent='center' mt={10}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth='lg' sx={{ mt: 4, mb: 6 }}>
      {/* ==================== ADD ADDON FORM ==================== */}
      <Card
        sx={{
          borderRadius: 4,
          p: 2,
          mb: 6,
          boxShadow: '0 8px 40px rgba(0,0,0,0.1)',
          background:
            'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(245,247,255,0.7))',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.5)',
        }}>
        <CardContent>
          <Typography
            variant='h5'
            align='center'
            sx={{
              mb: 3,
              fontWeight: 700,
              background: 'linear-gradient(90deg, #0072ff, #00c6ff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
            Addon Master
          </Typography>

          <Divider sx={{ mb: 3 }} />

          <Box display='flex' flexDirection='column' gap={2}>
            <TextField
              label='Addon Name'
              variant='outlined'
              fullWidth
              value={addonName}
              onChange={(e) => setAddonName(e.target.value)}
            />

            <TextField
              label='Price (incl. GST)'
              variant='outlined'
              type='number'
              fullWidth
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />

            <TextField
              label='GST %'
              variant='outlined'
              type='number'
              fullWidth
              value={gst}
              onChange={(e) => setGst(e.target.value)}
              helperText='Price is GST-inclusive; this rate is used for the tax breakdown.'
            />

            <Button
              variant='contained'
              size='large'
              onClick={saveAddon}
              disabled={saving}
              sx={{
                mt: 2,
                py: 1.2,
                fontWeight: 600,
                borderRadius: 2,
                background: 'linear-gradient(90deg, #0072ff, #00c6ff)',
                boxShadow: '0 4px 15px rgba(0, 114, 255, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(90deg, #0055cc, #00aaff)',
                },
              }}>
              {saving ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'Save Addon'
              )}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* #16 ==================== MAP ADD-ONS TO ITEMS ==================== */}
      <Card sx={{ borderRadius: 4, boxShadow: 3, mb: 4 }}>
        <CardContent>
          <Typography variant='h6' fontWeight={700} sx={{ mb: 0.5 }}>
            Map Add-ons to Menu Items
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Pick an item, tick which add-ons it offers. These show on the QR menu
            when a customer adds the item.
          </Typography>
          <TextField
            select
            SelectProps={{ native: true }}
            size='small'
            label='Item'
            value={mapItemId}
            onChange={(e) => pickMapItem(e.target.value)}
            sx={{ minWidth: 260, mb: 2 }}
            InputLabelProps={{ shrink: true }}>
            <option value=''>— Select item —</option>
            {menuItems.map((it) => (
              <option key={it.id} value={it.id}>
                {it.name}
              </option>
            ))}
          </TextField>

          {mapItemId ? (
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {addons.length === 0 ? (
                  <Typography color='text.secondary'>
                    No add-ons yet — add some above first.
                  </Typography>
                ) : (
                  addons.map((a) => {
                    const on = mapAddonIds.includes(a.id);
                    return (
                      <Chip
                        key={a.id}
                        label={`${a.name} (+${a.price})`}
                        color={on ? 'primary' : 'default'}
                        variant={on ? 'filled' : 'outlined'}
                        onClick={() =>
                          setMapAddonIds((prev) =>
                            on
                              ? prev.filter((x) => x !== a.id)
                              : [...prev, a.id],
                          )
                        }
                        sx={{ fontWeight: 600 }}
                      />
                    );
                  })
                )}
              </Box>
              <Button
                variant='contained'
                onClick={saveItemAddons}
                disabled={mapSaving}
                sx={{ borderRadius: 2, fontWeight: 700 }}>
                {mapSaving ? 'Saving…' : 'Save Item Add-ons'}
              </Button>
            </>
          ) : null}
        </CardContent>
      </Card>

      {/* ==================== ADDON REPORT ==================== */}
      <Card sx={{ borderRadius: 4, boxShadow: 3 }}>
        <CardContent sx={{ pb: 1 }}>
          <Typography variant='h6' fontWeight={600} sx={{ mb: 3 }}>
            All Addons
          </Typography>

          {/* Search + Export */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              mb: 3,
              alignItems: 'center',
            }}>
            <TextField
              label='Search Addon'
              size='small'
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 280 }}
            />

            <Button
              variant='outlined'
              startIcon={<FileDownloadIcon />}
              onClick={exportToExcel}
              disabled={filteredAddons.length === 0}
              sx={{ ml: 'auto' }}>
              Export Excel
            </Button>
          </Box>

          {/* Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell width={60}>#</TableCell>
                  <TableCell>Addon Name</TableCell>
                  <TableCell width={140}>Price (₹)</TableCell>
                  <TableCell width={90}>GST %</TableCell>
                  <TableCell width={180}>Created Date</TableCell>
                  <TableCell width={140}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedAddons.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align='center' sx={{ py: 8 }}>
                      <Typography color='text.secondary'>
                        {search.trim()
                          ? 'No addons match your search'
                          : 'No addons found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedAddons.map((addon, index) => (
                    <TableRow key={addon.id} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{addon.name || '—'}</TableCell>
                      <TableCell>
                        {addon.price !== undefined && addon.price !== null
                          ? `₹${Number(addon.price).toFixed(2)}`
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {addon.gst ? `${Number(addon.gst).toFixed(0)}%` : '—'}
                      </TableCell>
                      <TableCell>
                        {addon.created
                          ? new Date(addon.created).toLocaleDateString(
                              'en-IN',
                              {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                              },
                            )
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Button
                          size='small'
                          variant='contained'
                          onClick={() => openEdit(addon)}>
                          Update
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component='div'
            count={filteredAddons.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>

      {/* ==================== EDIT DIALOG ==================== */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth='sm'
        fullWidth>
        <DialogTitle>Update Addon</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            autoFocus
            margin='dense'
            label='Addon Name'
            fullWidth
            variant='outlined'
            value={editItem?.name || ''}
            onChange={(e) =>
              setEditItem((prev) => ({ ...prev, name: e.target.value }))
            }
            sx={{ mb: 2 }}
          />
          <TextField
            margin='dense'
            label='Price (incl. GST)'
            type='number'
            fullWidth
            variant='outlined'
            value={editItem?.price ?? ''}
            onChange={(e) =>
              setEditItem((prev) => ({ ...prev, price: e.target.value }))
            }
            sx={{ mb: 2 }}
          />
          <TextField
            margin='dense'
            label='GST %'
            type='number'
            fullWidth
            variant='outlined'
            value={editItem?.gst ?? ''}
            onChange={(e) =>
              setEditItem((prev) => ({ ...prev, gst: e.target.value }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button
            variant='contained'
            onClick={updateAddon}
            disabled={updating || !editItem?.name?.trim()}>
            {updating ? (
              <CircularProgress size={20} color='inherit' />
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
